import { createHttpClient } from '@adapters/HttpClient';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import type { LoadOptionsResponse } from '@shared/components/ui/MultiSelect';
import { apiClientWH } from '@shared/services/ApiClient';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { DateRange } from 'react-day-picker';
import { ProductsApiAdapter } from '../adapters/ProductsApiAdapter';
import { StockMovementsFiltersApiAdapter } from '../adapters/StockMovementsFiltersApiAdapter';
import { UsersApiAdapter } from '../adapters/UsersApiAdapter';
import { emptyReferenceIdArrays, REFERENCE_ID_COLUMNS, type ReferenceIdColumn } from '../utils/referenceIdFilters';

interface StockMovementsFiltersState {
  product_id: string[];
  team_id: string[];
  qty: { min: string; max: string };
  event_id: string[];
  description: string;
  user_id: string[];
  created_at: DateRange | undefined;
  order_id: string[];
  stock_location_order_id: string[];
  returned_reception_id: string[];
  entry_id: string[];
  extra_work_order_id: string[];
  location_order_id: string[];
  batch_id: string[];
}

interface UseStockMovementsFiltersProps {
  initialConditions?: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}

function formatDateForAPI(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function createEmptyState(): StockMovementsFiltersState {
  return {
    product_id: [],
    team_id: [],
    qty: { min: '', max: '' },
    event_id: [],
    description: '',
    user_id: [],
    created_at: undefined,
    ...emptyReferenceIdArrays(),
  };
}

export const useStockMovementsFilters = ({ initialConditions = [], onChange }: UseStockMovementsFiltersProps) => {
  const [filters, setFilters] = useState<StockMovementsFiltersState>(() => parseConditionsToState(initialConditions));

  const httpClient = createHttpClient(apiClientWH);
  const referenceFiltersAdapter = useMemo(() => new StockMovementsFiltersApiAdapter(httpClient), [httpClient]);

  const usersAdapter = useMemo(() => new UsersApiAdapter(httpClient), [httpClient]);
  const productsAdapter = useMemo(() => new ProductsApiAdapter(httpClient), [httpClient]);

  const loadTeamsForMultiSelect = useCallback(
    (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> =>
      referenceFiltersAdapter.loadTeams(params),
    [referenceFiltersAdapter],
  );

  const loadBatchesForMultiSelect = useCallback(
    (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> =>
      referenceFiltersAdapter.loadBatches(params),
    [referenceFiltersAdapter],
  );

  const { data: users = [], isLoading: isLoadingUsers } = useQuery({
    queryKey: ['stock-movements-filters-users'],
    queryFn: async () => {
      const result = await usersAdapter.getAll();
      return result.map((user) => ({
        value: user.id.toString(),
        label: user.name,
      }));
    },
  });

  const { data: allProducts = [], isLoading: isLoadingProducts } = useQuery({
    queryKey: ['stock-movements-filters-products-all'],
    queryFn: async () => {
      const products = await productsAdapter.getAllProducts();
      return products.map((p) => ({
        id: p.id,
        name: p.name,
        code: p.code,
        image: p.image_hash,
      }));
    },
    staleTime: 5 * 60 * 1000,
  });

  const searchProducts = useCallback(
    async (query: string) => {
      if (!allProducts.length) return [];

      const lowerQuery = query.toLowerCase().trim();
      const filtered = allProducts.filter(
        (p) =>
          !lowerQuery ||
          p.name.toLowerCase().includes(lowerQuery) ||
          (p.code && p.code.toLowerCase().includes(lowerQuery)),
      );

      return filtered.map((p) => ({
        id: Number(p.id),
        value: p.id.toString(),
        name: p.name,
        code: p.code,
        image: p.image,
      }));
    },
    [allProducts],
  );

  const loadOrdersForMultiSelect = useCallback(
    (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> =>
      referenceFiltersAdapter.loadOrders(params),
    [referenceFiltersAdapter],
  );

  const loadPreparationsForMultiSelect = useCallback(
    (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> =>
      referenceFiltersAdapter.loadPreparations(params),
    [referenceFiltersAdapter],
  );

  const loadReturnedReceptionsForMultiSelect = useCallback(
    (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> =>
      referenceFiltersAdapter.loadReturnedReceptions(params),
    [referenceFiltersAdapter],
  );

  const loadEntriesForMultiSelect = useCallback(
    (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> =>
      referenceFiltersAdapter.loadEntries(params),
    [referenceFiltersAdapter],
  );

  const loadExtraWorkOrdersForMultiSelect = useCallback(
    (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> =>
      referenceFiltersAdapter.loadExtraWorkOrders(params),
    [referenceFiltersAdapter],
  );

  const loadLocationOrdersForMultiSelect = useCallback(
    (params: { search: string; page: number; limit: number }): Promise<LoadOptionsResponse> =>
      referenceFiltersAdapter.loadLocationOrders(params),
    [referenceFiltersAdapter],
  );

  const convertStateToConditions = useCallback((state: StockMovementsFiltersState): FilterConditionOutput[] => {
    const conditions: FilterConditionOutput[] = [];

    if (state.product_id.length > 0) {
      conditions.push({ column: 'product_id', action: 'in', value: state.product_id });
    }

    if (state.team_id.length > 0) {
      conditions.push({ column: 'team_id', action: 'in', value: state.team_id });
    }

    if (state.qty.min) {
      conditions.push({ column: 'qty', action: 'greater_equal', value: state.qty.min });
    }
    if (state.qty.max) {
      conditions.push({ column: 'qty', action: 'less_equal', value: state.qty.max });
    }

    if (state.event_id.length > 0) {
      conditions.push({ column: 'event_id', action: 'in', value: state.event_id });
    }

    if (state.description.trim()) {
      conditions.push({
        column: 'description',
        action: 'contains',
        value: state.description.trim(),
      });
    }

    if (state.user_id.length > 0) {
      conditions.push({ column: 'user_id', action: 'in', value: state.user_id });
    }

    if (state.created_at?.from && state.created_at?.to) {
      conditions.push({
        column: 'created_at',
        action: 'greater_equal',
        value: formatDateForAPI(state.created_at.from),
      });
      conditions.push({
        column: 'created_at',
        action: 'less_equal',
        value: formatDateForAPI(state.created_at.to),
      });
    }

    REFERENCE_ID_COLUMNS.forEach((column) => {
      const values = state[column];
      if (values.length > 0) {
        conditions.push({ column, action: 'in', value: values });
      }
    });

    return conditions;
  }, []);

  const updateFilters = useCallback(
    (updates: Partial<StockMovementsFiltersState>) => {
      setFilters((prev) => {
        const newState = { ...prev, ...updates };
        onChange?.(convertStateToConditions(newState));
        return newState;
      });
    },
    [convertStateToConditions, onChange],
  );

  const handleProductChange = useCallback((values: string[]) => updateFilters({ product_id: values }), [updateFilters]);
  const handleTeamChange = useCallback((values: string[]) => updateFilters({ team_id: values }), [updateFilters]);
  const handleEventChange = useCallback((values: string[]) => updateFilters({ event_id: values }), [updateFilters]);
  const handleDescriptionChange = useCallback(
    (value: string) => updateFilters({ description: value }),
    [updateFilters],
  );
  const handleUserChange = useCallback((values: string[]) => updateFilters({ user_id: values }), [updateFilters]);
  const handleDateChange = useCallback(
    (range: DateRange | undefined) => updateFilters({ created_at: range }),
    [updateFilters],
  );

  const handleReferenceFilterChange = useCallback(
    (column: ReferenceIdColumn, values: string[]) => updateFilters({ [column]: values }),
    [updateFilters],
  );

  const handleQtyChange = useCallback(
    (type: 'min' | 'max', value: string) => {
      setFilters((prev) => {
        const newState = { ...prev, qty: { ...prev.qty, [type]: value } };
        onChange?.(convertStateToConditions(newState));
        return newState;
      });
    },
    [onChange, convertStateToConditions],
  );

  const clearAllFilters = useCallback(() => {
    const emptyState = createEmptyState();
    setFilters(emptyState);
    onChange?.([]);
  }, [onChange]);

  useEffect(() => {
    setFilters(parseConditionsToState(initialConditions));
  }, [initialConditions]);

  return {
    filters,
    handleProductChange,
    handleTeamChange,
    handleQtyChange,
    handleEventChange,
    handleDescriptionChange,
    handleUserChange,
    handleDateChange,
    handleReferenceFilterChange,
    clearAllFilters,
    loadBatchesForMultiSelect,
    loadOrdersForMultiSelect,
    loadPreparationsForMultiSelect,
    loadReturnedReceptionsForMultiSelect,
    loadEntriesForMultiSelect,
    loadExtraWorkOrdersForMultiSelect,
    loadLocationOrdersForMultiSelect,
    loadTeamsForMultiSelect,
    users,
    searchProducts,
    isLoadingUsers,
    isLoadingProducts,
  };
};

function parseConditionsToState(conditions: FilterConditionOutput[]): StockMovementsFiltersState {
  const state = createEmptyState();

  const byColumn: Record<string, FilterConditionOutput[]> = {};
  conditions.forEach((c) => {
    if (!byColumn[c.column]) byColumn[c.column] = [];
    byColumn[c.column].push(c);
  });

  const getArray = (col: string) => {
    const c = byColumn[col]?.find((x) => x.action === 'in');
    if (!c?.value) return [];
    return Array.isArray(c.value) ? c.value.map(String) : [String(c.value)];
  };

  state.product_id = getArray('product_id');
  state.team_id = getArray('team_id');
  state.event_id = getArray('event_id');
  state.user_id = getArray('user_id');

  REFERENCE_ID_COLUMNS.forEach((column) => {
    state[column] = getArray(column);
  });

  const qtyMin = byColumn['qty']?.find((c) => c.action === 'greater_equal');
  const qtyMax = byColumn['qty']?.find((c) => c.action === 'less_equal');
  if (qtyMin) state.qty.min = String(qtyMin.value);
  if (qtyMax) state.qty.max = String(qtyMax.value);

  const desc = byColumn['description']?.find((c) => c.action === 'contains');
  if (desc) state.description = String(desc.value);

  const dateMin = byColumn['created_at']?.find((c) => c.action === 'greater_equal');
  const dateMax = byColumn['created_at']?.find((c) => c.action === 'less_equal');
  if (dateMin?.value && dateMax?.value) {
    state.created_at = {
      from: new Date(String(dateMin.value)),
      to: new Date(String(dateMax.value)),
    };
  }

  return state;
}
